import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { memberIdentificationClient } from 'tradera-utils/http';
import { Trans, useTranslator } from 'tradera-lang/translate';
import { LocalLoader } from 'tradera-components/loader/loader';

const STATE = {
    VERIFYING: 'VERIFYING',
    ERROR_UNAVAILABLE: 'ERROR_UNAVAILABLE',
    CONTACT_CUSTOMER_SUPPORT: 'CONTACT_CUSTOMER_SUPPORT'
};

const addScriptToDom = zignSecWebSDKUrl => {
    const dataAttributeName = 'data-zign-sec-web-sdk-url-script';
    if (document.querySelector(`[${dataAttributeName}]`)) {
        return;
    }
    const js = document.createElement('script');
    js.setAttribute(dataAttributeName, '1');
    js.setAttribute('type', 'text/javascript');
    js.setAttribute('src', zignSecWebSDKUrl);
    document.body.appendChild(js);
};

export const DocumentScanning = ({ onVerifyComplete }) => {
    const zignSecWebSDKUrl = useSelector(
        state => state.initData.pageSpecificData.zignSecWebSDKUrl
    );
    const { t } = useTranslator();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        addScriptToDom(zignSecWebSDKUrl);
    }, [zignSecWebSDKUrl]);
    const [verifyingState, setVerifyingState] = useState(STATE.VERIFYING);

    useEffect(() => {
        if (verifyingState !== STATE.VERIFYING) {
            return;
        }

        let currentTimeoutId = null;
        const poll = (zs, sessionId) =>
            new Promise(resolve => {
                const call = () => {
                    currentTimeoutId = setTimeout(async () => {
                        try {
                            // Result will appear when verification is completed.
                            const { result } = await zs.getSessionStatus(
                                sessionId
                            );

                            if (result) {
                                return resolve();
                            }
                        } catch (error) {
                            console.error(error);
                        } finally {
                            if (currentTimeoutId) {
                                call();
                            }
                        }
                    }, 2000);
                };
                call();
            });
        setIsLoading(true);
        memberIdentificationClient
            .query('ScanningsCreateWebToken?purpose=SYI')
            .then(async ({ token, relayState }) => {
                setIsLoading(false);
                const zs = new window.ZignSec(token, window.ZsProduct.Scanning);

                const r = await zs.openInIframe('zs-root', {
                    relay_state: relayState,
                    analysis_types: ['document', 'selfie', 'fraud']
                });
                await poll(zs, r.id);

                setIsLoading(true);
                return await memberIdentificationClient.command(
                    'ScanningResult',
                    {
                        sessionId: r.id,
                        relayState: relayState
                    }
                );
            })
            .then(({ isSuccessful }) => {
                setIsLoading(false);

                if (isSuccessful) {
                    onVerifyComplete();
                } else {
                    setVerifyingState(STATE.CONTACT_CUSTOMER_SUPPORT);
                }
                return;
            })
            .catch(() => {
                setIsLoading(false);
                setVerifyingState(STATE.ERROR_UNAVAILABLE);
            });
        return () => {
            currentTimeoutId && clearTimeout(currentTimeoutId);
            currentTimeoutId = null;
        };
    }, [verifyingState, setVerifyingState, setIsLoading, onVerifyComplete]);

    return (
        <div className="document-scanning w-100 h-100 position-relative d-flex justify-content-center">
            {isLoading && <LocalLoader />}
            {verifyingState === STATE.VERIFYING && !isLoading && (
                <div id="zs-root" className="w-100 h-100" />
            )}
            {verifyingState === STATE.ERROR_UNAVAILABLE && (
                <span className="text-danger p-5">
                    {t('international_identification_errorUnavailable')}
                </span>
            )}
            {verifyingState === STATE.CONTACT_CUSTOMER_SUPPORT && (
                <div className="text-center p-5">
                    <h4 className="my-4">
                        {t(
                            'international_identification_contactCustomerSupportHeading'
                        )}
                    </h4>
                    <p className="mb-4 text-left px-4">
                        <Trans
                            i18nKey="international_identification_contactCustomerSupportText_Trans"
                            components={{ a: <a /> }}
                        />
                    </p>
                    <button
                        className="btn btn-primary"
                        onClick={() => setVerifyingState(STATE.VERIFYING)}>
                        {t('international_identification_buttonTryAgain')}
                    </button>
                </div>
            )}
        </div>
    );
};
